import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Avatar } from '@mui/material';
import { useLelyAuth } from '../../../auth/useLelyAuth';
import Icon from '../IconSvg/Icon';
import LelyNavigationMenu from '../muiWrappers/LelyNavigationMenu';
import { MenuNavigationItem } from '../muiWrappers/LelyNavigationListItem';
import UserSettings from '@/components/UserSettings/UserSettings';
import useEntityMenuItems from './hooks/useEntityMenuItems';
import useFeedback from './hooks/useFeedback';
import { getInitials } from './utils';

const HELP_URL =
  'https://lely.rightanswers.com/portal/app/portlets/results/viewsolution.jsp?solutionid=260922113729613';

function AppNavigation() {
  const { t } = useTranslation('menus');
  const { pathname } = useLocation();
  const { isAuthenticated, user } = useLelyAuth();
  const entityMenuItems = useEntityMenuItems();
  const openFeedback = useFeedback();

  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);

  // /settings opens the drawer; any other navigation closes it
  useEffect(() => {
    setIsUserSettingsOpen(pathname === '/settings');
  }, [pathname]);

  if (!isAuthenticated) return null;

  const nickname = user?.nickname || '';

  const navItem = (
    label: string,
    icon: string,
    path: string,
  ): MenuNavigationItem => ({
    label,
    icon: <Icon name={icon} />,
    testId: `nav-${path.replace(/^\//, '').replace(/\//g, '-')}`,
    to: path,
    isActive: pathname.startsWith(path),
  });

  const avatarItem: MenuNavigationItem = {
    label: nickname,
    testId: 'account-btn',
    icon: (
      <Avatar
        variant="square"
        sx={(theme) => ({
          bgcolor: 'transparent',
          width: 24,
          height: 24,
          fontSize: theme.lelyFontSize.MD,
        })}
      >
        {getInitials(nickname)}
      </Avatar>
    ),
    showChevron: true,
    onClick: () => setIsUserSettingsOpen((prev) => !prev),
  };

  // Users without LTDA access only get the avatar (user settings)
  const sections = user?.allowltda
    ? {
        topSectionItems: user.allowhub
          ? [
              navItem('Farms', 'lely-icon-farm', '/farms'),
              navItem('Hub devices', 'lely-icon-hub', '/hub-devices'),
            ]
          : undefined,
        middleSectionItems: entityMenuItems.length
          ? entityMenuItems
          : undefined,
        bottomSectionItems: [
          {
            label: t('help'),
            icon: <Icon name="lely-icon-help" />,
            link: HELP_URL,
          },
          {
            label: t('feedback'),
            icon: <Icon name="lely-icon-chat-bubble" />,
            showChevron: true,
            onClick: openFeedback,
          },
        ],
      }
    : {};

  return (
    <>
      <LelyNavigationMenu avatarItem={avatarItem} {...sections} />
      <UserSettings open={isUserSettingsOpen} setOpen={setIsUserSettingsOpen} />
    </>
  );
}

export default AppNavigation;
